<script setup>
import { setTrack } from '@/api/login'
import { useRouter } from 'vue-router'
import { goToExperience } from '@/utils/experience'
import DesignFooter from '@/components/DesignFooter.vue'
import {
  guestHomeLinks, heroImage, capabilities, businesses,
  advantages, algorithms, playgroundFeatures, openclawFeatures, products,
  partners, updates,
} from './guestHomeData.js'

const router = useRouter()

// public 文件使用部署 base，避免生产 /ai-open-platform/ 子路径下素材失效。
const asset = name =>
  `${import.meta.env.BASE_URL}image/${name.includes('/') ? name : `home-redesign/${name}`}`

// 与 Communication 的咨询埋点一致；不阻断链接默认行为或键盘/新标签打开。
const trackConsultation = () => {
  void Promise.resolve().then(() => setTrack({ position: 'consultation' })).catch(() => {})
}
</script>

<template>
  <div class="guest-home">
    <main aria-label="AI开放平台首页">
      <section class="guest-hero" aria-labelledby="guest-home-title">
        <img class="hero-image" :src="asset(heroImage)" alt="AI开放平台科技背景" loading="lazy" width="1920" height="416" />
        <div class="guest-container hero-content">
          <div>
            <h1 id="guest-home-title">AI开放平台</h1>
          </div>
          <p class="hero-description">提供端侧大模型、视觉和音频算法、模型转换工具及适配硬件。可在线验证算法效果、查看硬件适配信息、获取开发文档和SDK。</p>
          <p class="hero-summary">在线验证算法效果　/　匹配硬件与系统　/　获取开发资源</p>
        </div>
      </section>

      <section id="guest-capabilities" class="guest-section muted" aria-labelledby="guest-capabilities-title">
        <div class="guest-container">
          <h2 id="guest-capabilities-title" class="sr-only">核心能力</h2>
          <div class="guest-grid two-columns capability-grid">
            <article v-for="item in capabilities" :key="item.title" class="guest-card capability-card">
              <div class="capability-heading">
                <img :src="asset(item.image)" :alt="item.title + '图标'" loading="lazy" width="32" height="32" />
                <h3>{{ item.title }}</h3>
              </div>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-business" class="guest-section" aria-labelledby="guest-business-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-business-title">核心业务领域</h2>
            <p>覆盖家庭、商超、工业、城市等多场景，为各行各业提供全方位的AI软硬件整体解决方案。</p>
          </div>
          <div class="guest-grid four-columns">
            <article v-for="item in businesses" :key="item.title" class="business-card">
              <a :href="item.href" :aria-label="item.title + '解决方案'" v-track="{ position: 'homepage > solution', name: item.title }">
                <img :src="asset(item.image)" :alt="item.title + '应用场景'" loading="lazy" width="336" height="212" />
                <div class="business-copy">
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.desc }}</p>
                </div>
              </a>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-algostore" class="guest-section muted" aria-labelledby="guest-algostore-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-algostore-title">AlgoStore算法超市，让AIoT创新像“逛超市”一样简单</h2>
            <p>汇聚丰富前沿AI算法，为业务难题与产品创新解锁无限可能</p>
          </div>
          <div class="guest-grid three-columns">
            <article v-for="item in advantages" :key="item.title" class="guest-card advantage-card">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <a class="text-link" :href="guestHomeLinks.algorithms" v-track="'homepage > algorithm_market'" :aria-label="item.title + '：了解更多'">了解更多</a>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-algorithms" class="guest-section" aria-labelledby="guest-algorithms-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-algorithms-title">多种AI算法“即取即用”</h2>
            <p>提供全面的AI算法支持，涵盖计算机视觉、自然语言处理、音频算法等多个领域</p>
          </div>
          <div class="guest-grid four-columns">
            <article v-for="item in algorithms" :key="item.title" class="algorithm-card">
              <img :src="asset(item.image)" :alt="item.title + '效果示例'" loading="lazy" width="288" height="164" />
              <h3>{{ item.title }}</h3>
              <span class="guest-tag">{{ item.tag }}</span>
              <p class="algorithm-description">{{ item.desc }}</p>
              <div class="algorithm-scene"><p>适用场景</p><p>{{ item.scene }}</p></div>
              <a class="guest-button algorithm-button" v-track="{ position: 'homepage > algorithm > experience', name: item.title }" :href="item.href" @click.prevent="goToExperience(router, item.href)" :aria-label="'立即体验' + item.title">立即体验</a>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-playground" class="guest-section muted" aria-labelledby="guest-playground-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-playground-title">AI Playground用户体验平台</h2>
            <p>为行业用户提供“开箱即用”的软硬一体化AI功能体验</p>
          </div>
          <div class="guest-grid three-columns">
            <article v-for="item in playgroundFeatures" :key="item.title" class="guest-card playground-card">
              <h3>{{ item.title }}</h3><p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-openclaw" class="guest-section" aria-labelledby="guest-openclaw-title">
        <div class="guest-container">
          <div class="section-heading openclaw-heading">
            <h2 id="guest-openclaw-title">OpenClaw × AI Playground</h2>
            <p>无论是个人开发者还是企业创新团队，AI Playground都能提供全链路支持， 让复杂的AI技术高效转化为可落地的实践路径，加速AI创新与产品化进程。</p>
          </div>
          <div class="guest-grid two-columns">
            <article v-for="item in openclawFeatures" :key="item.title" class="guest-card openclaw-card">
              <h3>{{ item.title }}</h3><p>{{ item.desc }}</p>
            </article>
          </div>
          <div class="section-action"><a class="guest-button primary openclaw-button" :href="guestHomeLinks.playground" @click="trackConsultation">立即申请体验</a></div>
        </div>
      </section>

      <section id="guest-products" class="guest-section muted" aria-labelledby="guest-products-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-products-title">AI生态产品</h2>
            <p>提供从开发板到边缘计算设备再到训练平台的完整AI开发生态</p>
          </div>
          <div class="guest-grid product-grid">
            <article v-for="item in products" :key="item.title" class="guest-card product-card">
              <img :src="asset(item.image)" :alt="item.title + '硬件产品'" loading="lazy" width="400" height="226" />
              <h3>{{ item.title }}</h3>
              <span class="guest-tag">硬件产品</span>
              <p>{{ item.desc }}</p>
              <a class="text-link product-link" v-track="{ position: 'homepage > product', name: item.title }" :href="item.href" :aria-label="'了解' + item.title + '产品详情'">了解产品详情</a>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-partners" class="guest-section partner-section" aria-labelledby="guest-partners-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-partners-title">合作伙伴</h2>
            <p>我们与众多行业领先企业建立战略合作关系，共同推动AI技术创新与应用落地</p>
          </div>
          <ul class="partner-grid" aria-label="19家合作伙伴">
            <li v-for="item in partners" :key="item.name">
              <a :href="item.href" :aria-label="item.name + '官网'" v-track="{ position: 'homepage > partner', name: item.name }">
                <img :src="asset(item.image)" :alt="item.name" loading="lazy" width="144" height="80" />
              </a>
            </li>
          </ul>
          <div class="section-action"><a class="guest-button primary partner-button" v-track="'homepage > partners'" :href="guestHomeLinks.partners">了解生态合作计划</a></div>
        </div>
      </section>

      <section id="guest-updates" class="guest-section muted" aria-labelledby="guest-updates-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-updates-title">最新动态</h2>
            <p>了解平台最新动态、技术进展和市场活动信息</p>
          </div>
          <div class="guest-grid three-columns">
            <article v-for="item in updates" :key="item.href" class="guest-card news-card">
              <h3><a :href="item.href" v-track="{ position: 'homepage > news', name: item.title }">{{ item.title }}</a></h3>
              <div class="news-meta"><p>{{ item.category }}</p><time :datetime="item.date">{{ item.date }}</time></div>
              <a class="news-link" v-track="{ position: 'homepage > news', name: item.title }" :href="item.href" :aria-label="'阅读全文：' + item.title">阅读全文</a>
            </article>
          </div>
        </div>
      </section>

      <section id="guest-contact" class="guest-section" aria-labelledby="guest-contact-title">
        <div class="guest-container">
          <div class="section-heading">
            <h2 id="guest-contact-title">即刻开启AI创新之旅！</h2>
            <p>注册即享7天免费试用，解锁AI赋能业务的无限可能</p>
          </div>
          <div class="contact-actions">
            <a class="guest-button primary" :href="guestHomeLinks.register" v-track="'homepage > register'">免费注册</a>
            <a class="guest-button" :href="guestHomeLinks.contact" target="_blank" rel="noopener noreferrer" @click="trackConsultation">联系我们</a>
          </div>
        </div>
      </section>
    </main>

    <DesignFooter/>
  </div>
</template>

<style scoped>
.guest-home { width: 100%; min-width: 0; color: #1a1a1a; background: #fff; font-family: Stratos-Regular, 'Source Han Sans', Arial, sans-serif, -apple-system, BlinkMacSystemFont; -webkit-font-smoothing: antialiased; }
.guest-home *, .guest-home *::before, .guest-home *::after { box-sizing: border-box; }
.guest-home h1, .guest-home h2, .guest-home h3, .guest-home p, .guest-home ul { margin: 0; padding: 0; }
.guest-home ul { list-style: none; }
.guest-home a { color: inherit; text-decoration: none; }
.guest-home a:hover { color: #f00018; }
.guest-home a:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
.guest-home img { display: block; max-width: 100%; }
.guest-home h2 { font-size: 22px; line-height: 1.2; font-weight: 700; }
.guest-home h3 { font-size: 16px; line-height: 1.2; font-weight: 700; }
.guest-home p { color: #666; font-size: 14px; line-height: normal; }
.guest-container { width: min(1440px, calc(100% - 48px)); margin-inline: auto; }
.guest-section { padding-block: 80px; }
.muted { background: #f5f5f5; }
.section-heading { display: flex; flex-direction: column; gap: 16px; text-align: center; margin-bottom: 32px; }
.section-heading p { font-size: 14px; line-height: normal; color: #6f6f6f; }
.guest-grid { display: grid; gap: 24px; align-items: stretch; }
.two-columns { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.three-columns { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.four-columns { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 32px; }
.guest-card { display: flex; flex-direction: column; gap: 16px; padding: 32px; border-radius: 12px; background: #fff; min-width: 0; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }
.guest-hero { position: relative; min-height: 416px; display: flex; align-items: center; padding-block: 64px; background: #14233b; }
.hero-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
.guest-hero::after { content: ''; position: absolute; inset: 0; background: rgba(13, 24, 43, 0.6); pointer-events: none; }
.hero-content { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 16px; }
.guest-home h1 { color: #fff; font-size: 32px; line-height: 1.2; font-weight: 700; }
.guest-home .hero-description { color: #fff; max-width: 1000px; font-size: 16px; line-height: 24px; }
.guest-home .hero-summary { color: #fff; font-size: 14px; line-height: 22px; }
.capability-grid { row-gap: 40px; }
.capability-card { justify-content: center; }
.capability-heading { display: flex; gap: 12px; align-items: center; min-height: 36px; }
.capability-heading img { width: 32px; height: 32px; flex-shrink: 0; }
.capability-heading h3 { font-size: 17px; }
.capability-card p { font-size: 14px; line-height: 20px; }
.business-card p { font-size: 14px; line-height: 1.25; }
#guest-business .section-heading p, #guest-products .section-heading p { line-height: 24px; }
.advantage-card p, .algorithm-card p, .playground-card p { font-size: 15px; line-height: 1.5; }
.business-card { min-width: 0; border-radius: 12px; background: #f4f7fb; overflow: hidden; }
.business-card > a { display: block; height: 100%; }
.business-card img { width: 100%; aspect-ratio: 336 / 212; height: auto; object-fit: cover; border-radius: 16px 16px 0 0; }
.business-copy { display: flex; flex-direction: column; gap: 16px; padding: 24px 24px 40px; }
.business-copy p { color: #6f6f6f; }
.advantage-card { gap: 20px; }
.text-link { display: inline-flex; align-items: center; min-height: 44px; align-self: flex-start; font-size: 13px; line-height: normal; color: #666 !important; }
.text-link:hover { color: #f00018 !important; text-decoration: underline; }
.advantage-card .text-link { margin-top: auto; }
.algorithm-card { min-width: 0; padding: 24px; border-radius: 12px; background: #f4f7fb; display: flex; flex-direction: column; gap: 16px; }
.algorithm-card > img { width: 100%; height: auto; aspect-ratio: 288 / 164; object-fit: contain; border-radius: 8px; }
.guest-tag { background: #eaeeee; border-radius: 999px; padding: 4px 10px; color: #14233b; align-self: flex-start; font-size: 12px; line-height: 1.5; }
.algorithm-scene { display: flex; flex-direction: column; gap: 8px; }
.algorithm-scene p { font-size: 12px; line-height: 1.25; }
.algorithm-scene p:first-child { color: #1a1a1a; font-size: 14px; line-height: normal; }
.guest-button { display: inline-flex; justify-content: center; align-items: center; min-height: 52px; padding: 10px 28px; min-width: 192px; border: 1px solid #e5e5e5; border-radius: 4px; background: #fff; font-size: 16px; line-height: 1; font-weight: 700; text-align: center; }
.guest-home .primary { color: #fff; background: #f00018; }
.guest-home .primary:hover { color: #fff; background: #d90016; }
.algorithm-button { min-width: 0; min-height: 48px; width: 100%; border-radius: 8px; margin-top: auto; font-size: 16px; line-height: 1.25; }
.openclaw-heading p { line-height: normal; }
.openclaw-card { background: #f4f7fb; }
.openclaw-card h3 { font-size: 16px; }
.openclaw-card p { font-size: 14px; line-height: 1.7; }
.section-action { margin-top: 32px; display: flex; justify-content: center; }
.openclaw-button { width: 248px; border-radius: 8px; font-size: 18px; }
.product-grid { grid-template-columns: repeat(2, minmax(0, calc((100% - 48px) / 3))); justify-content: center; }
.product-card { gap: 20px; }
.product-card > img { width: 100%; height: auto; aspect-ratio: 400 / 226; object-fit: cover; border-radius: 12px; }
.product-card > p { min-height: 0; }
.product-link { min-height: 48px; margin-top: auto; font-size: 14px; line-height: 24px; }
.partner-section { padding-block: 64px; }
.partner-section h2 { color: #14233b; }
.partner-section .section-heading p { color: #617089; font-size: 14px; line-height: 1.5; }
.partner-grid { display: grid; grid-template-columns: repeat(9, minmax(0, 1fr)); column-gap: 16px; row-gap: 16px; padding-inline: 8px !important; }
.partner-grid li { min-width: 0; }
.partner-grid a { display: flex; justify-content: center; align-items: center; height: 80px; padding: 4px; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 4px; }
.partner-grid img { width: 100%; height: 100%; min-width: 0; object-fit: contain; }
.partner-button { font-size: 16px; line-height: 1; min-height: 48px; font-weight: 500; border-radius: 8px; }
.news-card { min-height: 240px; gap: 20px; }
.news-card h3 { line-height: normal; min-height: 60px; }
.news-meta, .news-meta p { color: #666; font-size: 12px; line-height: normal; }
.news-meta p { line-height: 1.25; }
.news-link { display: inline-flex; align-items: center; min-height: 44px; font-size: 16px; line-height: normal; font-weight: 700; align-self: flex-start; margin-top: auto; }
#guest-updates h2 { line-height: normal; }
#guest-contact h2 { font-size: 30px; line-height: normal; }
#guest-contact .section-heading p { font-size: 18px; line-height: normal; }
.contact-actions .guest-button { font-size: 16px; line-height: 1.5714; }
.contact-actions { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
@media (max-width: 1199px) {
  .four-columns { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .three-columns { gap: 20px; }
  .guest-card { padding: 24px; }
  .partner-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
  .guest-container { width: calc(100% - 32px); }
  .guest-section, .partner-section { padding-block: 48px; }
  .guest-hero { min-height: 360px; padding-block: 48px; }
  .guest-home h2 { overflow-wrap: anywhere; }
  .section-heading { gap: 12px; margin-bottom: 24px; }
  .two-columns, .three-columns, .four-columns { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .product-grid { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .guest-card, .algorithm-card { padding: 24px; min-height: 0; }
  .business-card { min-height: 0; }
  .advantage-card p, .algorithm-description, .product-card > p, .news-card h3 { min-height: 0; }
  .guest-button { min-width: 0; }
  .contact-actions .guest-button { flex: 1 1 160px; max-width: 240px; }
  .partner-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; padding-inline: 0 !important; }
  .partner-grid a { height: 64px; }
}
/* 字体断点沿用原首页；以上交付稿布局断点保持不变。 */
@media (min-width: 768px) {
  .guest-home h1 { font-size: 36px; }
  .capability-heading h3 { font-size: 18px; }
  .partner-section h2 { font-size: 26px; }
}
@media (min-width: 992px) {
  .guest-home h1 { font-size: 46px; }
  .guest-home main h2 { font-size: 26px; }
  .guest-home .partner-section h2 { font-size: 28px; }
  .capability-heading h3 { font-size: 20px; }
  .algorithm-scene p:first-child { font-size: 16px; }
}
@media (min-width: 1200px) {
  .guest-home h1 { font-size: 50px; }
  .guest-home main h2 { font-size: 28px; }
  .guest-home .partner-section h2 { font-size: 30px; }
  .guest-home main h3 { font-size: 18px; }
  .guest-home main .capability-heading h3 { font-size: 24px; }
  .capability-card p, .business-card p, .product-card > p { font-size: 16px; }
  .product-link { font-size: 16px; }
  .partner-button { font-size: 18px; }
  .guest-home .openclaw-card h3 { font-size: 16px; }
}
@media (min-width: 1400px) {
  .guest-home h1 { font-size: 56px; }
  .guest-home main h2 { font-size: 30px; }
}
</style>